import React, { useEffect, useRef, useState } from 'react';
import { AutoComplete, Button, Form, Input } from 'antd';
import { useToast } from '../../../ui/ToastProvider';
import { putNurseProfile } from '../../../api';

const AddressInfoStep = ({ onNext, onBack, initial, onSaved }) => {
  const [form] = Form.useForm();
  const { notify } = useToast();
  const [loading, setLoading] = useState(false);
  const [addrOptions, setAddrOptions] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const [coordinates, setCoordinates] = useState([]);
  const [searchText, setSearchText] = useState('');
  const searchTimer = useRef();
  const parseAddress = (addr, displayName) => {
    const streetParts = [];
    if (addr.house_number) streetParts.push(addr.house_number);
    if (addr.road) streetParts.push(addr.road);
    let street = streetParts.join(' ').trim() || addr.suburb || addr.neighbourhood || '';
    if (!street && displayName) {
      street = displayName.split(',')[0];
    }
    const city = addr.city || addr.town || addr.village || addr.hamlet || addr.municipality || addr.county || '';
    const state = addr.state || addr.region || addr.state_district || '';
    const zipCode = addr.postcode || '';
    const country = addr.country || 'United States';
    return { street, city, state, zipCode, country };
  };
  const handleSearch = (val) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!val || val.length < 3) {
      setAddrOptions([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        setAddrLoading(true);
        const resp = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(val)}&format=json&addressdetails=1&limit=5&countrycodes=us`);
        const data = await resp.json();
        const opts = (data || []).map((r) => ({ value: String(r.place_id), label: r.display_name, data: r }));
        setAddrOptions(opts);
      } catch (e) {
        // ignore
      } finally {
        setAddrLoading(false);
      }
    }, 300);
  };
  const handleSelect = (value, option) => {
    const r = option?.data;
    if (r?.address) {
      const parsed = parseAddress(r.address, r.display_name);
      form.setFieldsValue(parsed);
      if (r.lat && r.lon) {
        setCoordinates([parseFloat(r.lon), parseFloat(r.lat)]);
      }
    }
    setSearchText('');
  };
  useEffect(() => {
    if (initial?.address) {
      form.setFieldsValue({
        street: initial.address.street,
        city: initial.address.city,
        state: initial.address.state,
        zipCode: initial.address.zipCode,
        country: initial.address.country,
      });
      if (initial.address.coordinates) {
        setCoordinates(initial.address.coordinates);
      }
    } else {
      form.resetFields(['street', 'city', 'state', 'zipCode', 'country']);
      setCoordinates([]);
    }
  }, [initial]);
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = { ...values, coordinates };
      await putNurseProfile({ address: payload });
      if (onSaved) onSaved({ address: payload });
      notify({ title: 'Address saved', description: 'Your address details were updated.', type: 'success' });
      onNext();
    } catch (e) {
      notify({ title: 'Failed to save address', description: 'Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };
  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.2 }}>Address</div>
        <div style={{ color: '#667085' }}>Tell us where you are located.</div>
      </div>
      <Form.Item label="Search Address">
        <AutoComplete
          value={searchText}
          options={addrOptions}
          onChange={(val) => {
            setSearchText(val);
            handleSearch(val);
          }}
          onSelect={handleSelect}
          placeholder="Type at least 3 characters to search"
          notFoundContent={addrLoading ? 'Searching...' : null}
          style={{ width: '100%', height: 60 }}
        />
      </Form.Item>
      <Form.Item name="street" label="Street" rules={[{ required: true }]}>
        <Input style={{ height: 60 }} />
      </Form.Item>
      <Form.Item name="city" label="City" rules={[{ required: true }]}>
        <Input style={{ height: 60 }} />
      </Form.Item>
      <Form.Item name="state" label="State" rules={[{ required: true }]}>
        <Input style={{ height: 60 }} />
      </Form.Item>
      <Form.Item name="zipCode" label="Zip Code" rules={[{ required: true }]}>
        <Input style={{ height: 60 }} />
      </Form.Item>
      <Form.Item name="country" label="Country" rules={[{ required: true }]}>
        <Input style={{ height: 60 }} />
      </Form.Item>
      <div style={{ marginTop: 32, paddingTop: 16, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {onBack ? (
          <Button htmlType="button" onClick={onBack} type="text" icon={<div style={{ marginRight: 6 }}>←</div>} style={{ color: '#667085', fontSize: 16, display: 'flex', alignItems: 'center' }}>Back</Button>
        ) : <div />}
        <Button type="primary" htmlType="submit" loading={loading} style={{
          height: 48,
          padding: '0 32px',
          fontSize: 16,
          borderRadius: 8,
          backgroundColor: '#004d54',
          borderColor: '#004d54',
          boxShadow: '0 4px 12px rgba(0, 77, 84, 0.15)'
        }}>
          Continue
        </Button>
      </div>
    </Form>
  );
};

export default AddressInfoStep;


